import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Pagination, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, fromUnixTime } from 'date-fns';
import DropdownFilter from '../../../../../input/DropdownFilter';
import { useStyles } from './SalesTaxReport.styles';
import { useStyles as useCommonStyles } from '../ReportSubItems.styles';
import InfoDisplay from '../../../../../layout/InfoDisplay';
import { Loading } from '../../../../../layout/Loading';
import { ReportUnavailable } from '../../../../../layout/ReportUnavailable';
import { notifyError } from '../../../../../../utils/bugsnag/notifyError';
import {
  getSalesTaxSummary,
  getSalesTaxOrders,
} from '../../../../../../services/ReportService';
import { useMenuContext } from '../../../../../../context/menuContext';
import { formatCurrencyDisplay } from '../../../../../../utils/FormatUtils/formatCurrencyValue';
import { useTableTax } from './SalesTaxReport.hook';
import { DatePicker } from '../../../../../input/DatePicker';
import {
  lastMonth,
  last3Months,
  yearToDate,
  thisWeek,
} from '../../../../../../utils/date-utils';
import { setDate } from '../../../../../../utils/FormatUtils/formatResponse';

export function BucketRow({ info, classes, restaurantId }) {
  const [open, setOpen] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [orders, setOrders] = useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(false);
  const [ordersErrorRequestId, setOrdersErrorRequestId] = useState(null);

  const breakdown = info.taxBreakdown || [];
  const hasBreakdown = breakdown.length > 0;
  const canDrillDown =
    restaurantId != null &&
    info.bucketStart != null &&
    info.bucketEnd != null;
  const expandable = hasBreakdown || canDrillDown;

  useEffect(() => {
    setShowOrders(false);
    setOrders(null);
    setOrdersPage(1);
    setOrdersTotalPages(1);
    setOrdersError(false);
    setOrdersErrorRequestId(null);
    setOrdersLoading(false);
  }, [restaurantId, info.bucketStart, info.bucketEnd]);

  useEffect(() => {
    if (!showOrders || !canDrillDown) return;
    let cancelled = false;
    setOrdersLoading(true);
    setOrdersError(false);
    setOrdersErrorRequestId(null);
    (async () => {
      try {
        const res = await getSalesTaxOrders(restaurantId, {
          start: fromUnixTime(info.bucketStart),
          end: fromUnixTime(info.bucketEnd),
          page: ordersPage,
        });
        if (cancelled) return;
        setOrders(res.results || []);
        setOrdersTotalPages(res.total_pages || 1);
      } catch (e) {
        if (!cancelled) {
          notifyError(e, {
            component: 'SalesTaxReport.BucketRow',
            method: 'fetchOrders',
          });
          setOrdersErrorRequestId(
            e?.requestId || e?.__apiContext?.requestId || null
          );
          setOrdersError(true);
        }
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showOrders,
    ordersPage,
    info.bucketStart,
    info.bucketEnd,
    restaurantId,
    canDrillDown,
  ]);

  const handleToggle = () => {
    setOpen((o) => {
      if (o) {
        setShowOrders(false);
        setOrders(null);
        setOrdersPage(1);
        setOrdersError(false);
        setOrdersErrorRequestId(null);
      }
      return !o;
    });
  };

  const retryOrdersFetch = () => {
    setOrdersError(false);
    setOrdersErrorRequestId(null);
    setOrdersPage((p) => p);
    setShowOrders(false);
    setTimeout(() => setShowOrders(true), 0);
  };

  return (
    <Box className={classes.bucketWrapper}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={expandable ? handleToggle : undefined}
        style={{ cursor: expandable ? 'pointer' : 'default' }}
        data-testid="bucket-row"
      >
        <Box display="flex" alignItems="center" flex="1">
          {expandable ? (
            <IconButton
              size="small"
              aria-label={open ? 'Collapse breakdown' : 'Expand breakdown'}
              style={{ padding: 2, marginRight: 4 }}
            >
              {open ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box style={{ width: 28 }} />
          )}
          <Box display="flex" justifyContent="space-between" flex="1">
            <Typography className={classes.taxesInfo}>{info.date}</Typography>
            <Typography className={classes.taxesInfo}>
              {formatCurrencyDisplay(info.sales)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" flex="1">
          <Typography
            className={classes.taxesInfo}
            style={{ textAlign: 'end', opacity: 1 }}
          >
            {formatCurrencyDisplay(info.paid)}
          </Typography>
        </Box>
      </Box>
      {open && expandable && (
        <Box className={classes.breakdownWrapper} data-testid="tax-breakdown">
          {breakdown.map((b) => (
            <Box key={b.label} className={classes.breakdownRow}>
              <Typography className={classes.breakdownLabel}>
                {b.label}
                {b.rate != null && (
                  <span className={classes.breakdownRate}>
                    {' '}
                    · {(b.rate * 100).toFixed(3)}%
                  </span>
                )}
              </Typography>
              <Typography className={classes.breakdownAmount}>
                {formatCurrencyDisplay(b.amount)}
              </Typography>
            </Box>
          ))}
          {canDrillDown && !showOrders && (
            <Box className={classes.viewOrdersRow}>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOrders(true);
                }}
                data-testid="view-orders-button"
              >
                View orders
              </Button>
            </Box>
          )}
          {showOrders && (
            <Box
              className={classes.ordersPanel}
              data-testid="orders-panel"
              onClick={(e) => e.stopPropagation()}
            >
              {ordersLoading && <Loading />}
              {ordersError && !ordersLoading && (
                <ReportUnavailable
                  requestId={ordersErrorRequestId}
                  onRetry={retryOrdersFetch}
                />
              )}
              {!ordersLoading && !ordersError && orders && orders.length === 0 && (
                <Typography className={classes.ordersEmpty}>
                  No orders contributed to this bucket.
                </Typography>
              )}
              {!ordersLoading && !ordersError && orders && orders.length > 0 && (
                <>
                  <Box className={classes.ordersHeader}>
                    <Typography className={classes.ordersColumnTitle}>
                      Order ID
                    </Typography>
                    <Typography className={classes.ordersColumnTitle}>
                      Transaction Date
                    </Typography>
                    <Typography className={classes.ordersColumnTitle}>
                      Jurisdiction
                    </Typography>
                    <Typography
                      className={classes.ordersColumnTitle}
                      style={{ textAlign: 'end' }}
                    >
                      Tax Collected
                    </Typography>
                  </Box>
                  {orders.map((o) => (
                    <Box
                      key={o.orderId}
                      className={classes.ordersRow}
                      data-testid="orders-row"
                    >
                      <Typography className={classes.ordersCell}>
                        {o.orderId}
                      </Typography>
                      <Typography className={classes.ordersCell}>
                        {o.transactionDate
                          ? format(
                              fromUnixTime(o.transactionDate),
                              'dd MMM yyyy'
                            )
                          : '—'}
                      </Typography>
                      <Typography className={classes.ordersCell}>
                        {o.jurisdiction || '—'}
                      </Typography>
                      <Typography
                        className={classes.ordersCell}
                        style={{ textAlign: 'end' }}
                      >
                        {formatCurrencyDisplay(o.taxCollected)}
                      </Typography>
                    </Box>
                  ))}
                  {ordersTotalPages > 1 && (
                    <Pagination
                      page={ordersPage}
                      count={ordersTotalPages}
                      size="small"
                      onChange={(_, value) => setOrdersPage(value)}
                      style={{ margin: '10px auto 0' }}
                      data-testid="orders-pagination"
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

BucketRow.propTypes = {
  info: PropTypes.shape({
    date: PropTypes.string,
    sales: PropTypes.number,
    paid: PropTypes.number,
    taxBreakdown: PropTypes.array,
    bucketStart: PropTypes.number,
    bucketEnd: PropTypes.number,
    orders: PropTypes.number,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  restaurantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BucketRow.defaultProps = {
  restaurantId: null,
};

export function Table() {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const [filter, setFilter] = useState('Year to date');
  const [page, setPage] = useState(1);
  const { selectedRestaurant } = useMenuContext();

  const { data, loading, error, errorObject, refetch, totalPages } = useTableTax({
    id: selectedRestaurant?.id,
    params: {
      'This week': thisWeek(),
      'Last month': lastMonth(),
      'Last 3 months': last3Months(),
      'Year to date': yearToDate(),
    }[filter],
    page,
  });

  useEffect(() => {
    if (error && errorObject) {
      notifyError(errorObject, {
        component: 'SalesTaxReport.Table',
        method: 'useTableTax',
      });
    }
  }, [error, errorObject]);

  if (error) {
    const tableRequestId =
      errorObject?.requestId || errorObject?.__apiContext?.requestId || null;
    return (
      <ReportUnavailable onRetry={refetch} requestId={tableRequestId} />
    );
  }
  const handleChangePage = (_, value) => {
    setPage(value);
  };

  return (
    <Box
      className={commonClasses.infoContainer}
      style={{ height: '518px', paddingBottom: '0px' }}
      marginBottom="20px"
    >
      <Box display="flex" justifyContent="space-between" marginBottom="32px">
        <Typography className={classes.taxesTitle}>Tax Detail</Typography>
        <DropdownFilter
          onlyString
          items={{
            filters: ['This week', 'Last month', 'Last 3 months', 'Year to date'],
            filter,
            setFilter,
          }}
          style={{ marginLeft: '24px' }}
        />
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            marginBottom="20px"
          >
            <Box display="flex" alignItems="center" flex="1">
              <Box style={{ width: 32 }} />
              <Box display="flex" justifyContent="space-between" flex="1">
                <Typography className={classes.taxesColumnTitle}>
                  Date
                </Typography>
                <Typography className={classes.taxesColumnTitle}>
                  Sales
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" flex="1">
              <Typography
                className={classes.taxesColumnTitle}
                style={{ textAlign: 'end' }}
              >
                Your Liability
              </Typography>
            </Box>
          </Box>
          {data.table_info.map((info) => (
            <BucketRow
              key={`${selectedRestaurant?.id ?? 'no-rest'}-${
                info.sort ?? info.bucketStart ?? info.date
              }`}
              info={info}
              classes={classes}
              restaurantId={selectedRestaurant?.id}
            />
          ))}
        </>
      )}
      {totalPages > 0 && (
        <Pagination
          page={page}
          count={totalPages}
          size="small"
          onChange={handleChangePage}
          style={{ margin: '15px auto' }}
        />
      )}
    </Box>
  );
}

export function SalesTaxReport() {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const [filter, setFilter] = useState({ label: 'Last month' });
  const { selectedRestaurant } = useMenuContext();
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(true);
  const [error, setError] = useState(false);
  const [errorRequestId, setErrorRequestId] = useState(null);
  const [data, setData] = useState();
  const [popoverEl, setPopoverEl] = useState(null);
  const [params, setParams] = useState(lastMonth());
  const [selectedSalesDateTxt, setSelectedSalesDateTxt] = useState('');
  const filterBtnEl = useRef();

  useEffect(() => {
    if (filter.label === 'This week') {
      const startUnix = Math.floor(new Date(params.start).getTime() / 1000);
      const dateStart = setDate(startUnix, 0, 'day');
      const endUnix = Math.floor(new Date(params.end).getTime() / 1000);
      const dateEnd = setDate(endUnix, 0, 'day');
      setSelectedSalesDateTxt(dateStart + ' - ' + dateEnd);
    } else if (
      filter.label === 'Last month' ||
      filter.label === 'Last 3 months' ||
      filter.label === 'Year to date'
    ) {
      const startUnix = Math.floor(new Date(params.start).getTime() / 1000);
      const date = setDate(startUnix, 0, 'month');
      setSelectedSalesDateTxt(date);
    } else if (filter.label === 'Custom...') {
      const startUnix = Math.floor(new Date(params.start).getTime() / 1000);
      const dateStart = setDate(startUnix, 0, 'day');
      setSelectedSalesDateTxt(dateStart);

      const oneDayMs = 24 * 60 * 60 * 1000;
      const endMinusOne = new Date(new Date(params.end).getTime() - oneDayMs);
      const endUnix = Math.floor(endMinusOne.getTime() / 1000);
      const dateEnd = setDate(endUnix, 0, 'day');
      setSelectedSalesDateTxt(dateStart + ' - ' + dateEnd);
    }
  }, [filter, params]);

  const handlePopoverOpen = () => {
    setPopoverEl(filterBtnEl.current);
  };

  const handlePopoverClose = () => {
    setPopoverEl(null);
  };

  useEffect(() => {
    if (filter.label !== 'Custom...') {
      const newParams = {
        'This week': thisWeek(),
        'Last month': lastMonth(),
        'Last 3 months': last3Months(),
        'Year to date': yearToDate(),
      }[filter.label];

      if (newParams) setParams(newParams);
    }
  }, [filter]);

  useEffect(() => {
    setReloading(true);
    setError(false);
    setErrorRequestId(null);
    if (selectedRestaurant?.id)
      (async () => {
        try {
          const summary = await getSalesTaxSummary(
            selectedRestaurant?.id,
            params
          );
          setData(summary);
          setLoading(false);
          setReloading(false);
        } catch (err) {
          notifyError(err, {
            component: 'SalesTaxReport',
            method: 'fetchSummary',
          });
          setErrorRequestId(err?.requestId || err?.__apiContext?.requestId || null);
          setError(true);
          setLoading(false);
          setReloading(false);
        }
      })();
  }, [selectedRestaurant, params]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ReportUnavailable requestId={errorRequestId} />;
  }

  return (
    <>
      <Box flex="1">
        <Box className={commonClasses.header}>
          <Typography variant="h1" className={commonClasses.pageTitle}>
            Tax &amp; Compliance
          </Typography>
          <Box className={classes.filterRow}>
            {selectedRestaurant?.timezone && (
              <Typography
                className={classes.timezoneLabel}
                data-testid="timezone-label"
              >
                Times in {selectedRestaurant.timezone}
              </Typography>
            )}
            <Box ref={filterBtnEl}>
              <DropdownFilter
                items={{
                  filters: [
                    'This week',
                    'Last month',
                    'Last 3 months',
                    'Year to date',
                    {
                      label: 'Custom...',
                      open: handlePopoverOpen,
                      close: handlePopoverClose,
                    },
                  ],
                  filter,
                  setFilter,
                }}
                className={classes.dropdownFilter}
              />
            </Box>
          </Box>
        </Box>
        <Box className={commonClasses.infoWrapper}>
          {reloading ? (
            <Box
              height="146px"
              display="flex"
              alignItems="center"
              flex={1}
              justifyContent="center"
            >
              <Loading />
            </Box>
          ) : (
            <>
              <InfoDisplay
                info={{
                  title: 'Tax Collected from Customers',
                  info: formatCurrencyDisplay(data.info.collected),
                }}
              />
              <InfoDisplay
                info={{
                  title: 'Remitted by Eatzy',
                  info: formatCurrencyDisplay(
                    Math.max(0, data.info.collected - data.info.remitted)
                  ),
                }}
              />
              <InfoDisplay
                info={{
                  title: 'Your Remaining Liability',
                  info: formatCurrencyDisplay(data.info.remitted),
                }}
              />
            </>
          )}
        </Box>
        <Table />
      </Box>
      <DatePicker
        popoverEl={popoverEl}
        handleClosePopover={handlePopoverClose}
        setParams={setParams}
      />
    </>
  );
}

export default SalesTaxReport;
